import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  Tooltip,
  InputAdornment,
  Divider,
  Typography,
  CircularProgress,
} from "@mui/material";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import { GetStateCityPTHelper } from "../helper/WorkLocation";
import {
  AddWorkLocationAction,
  UpdateWorkLocationAction,
} from "../../../../../store/Ancillary/WorkLocation/Action";
import GeoLocationPicker from "../helper/GeoLocationPicker";
import Popup from "../../../Base/views/Popup";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";

const initialValue = {
  work_location_name: "",
  state_id: "",
  professional_tax_region_id: "",
  is_default: false,
  lat_long: "",
  radius: "",
};
const WorkGeoLocationConfig = ({ editedRecord, setOpenPopup }) => {

  const dispatch = useDispatch();
  const { workLocation_options } = useSelector(
    (state) => state?.AncillaryReducer?.WorkLocationReducer
  );

  const [form, setForm] = useState({
    ...initialValue,
    is_default: (workLocation_options?.length || 0) === 0,
  });
  const [errors, setErrors] = useState({});
  const [stateCityPTData, setStateCityPTData] = useState([]);
  const [stateData, setStateData] = useState({ load: true, values: [] });
  const [regionData, setRegionData] = useState({ load: true, values: [] });
  const [expand, setExpand] = useState(false);
  const [latLongInput, setLatLongInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    GetStateCityPTHelper().then((res) => {
      if (res?.data?.statuscode === 200) {
        setStateCityPTData(res?.data?.data?.[0] || []);
      }
    });
  }, []);
  useEffect(() => {
    if (editedRecord) {
      setForm(editedRecord);
      if (editedRecord?.lat_long) {
        setLatLongInput(editedRecord.lat_long);
      }
      setRegionData({
        load: false,
        values:
          stateCityPTData?.result?.filter(
            (el) => el?.state_id === editedRecord?.state_id?.state_id
          ) || [],
      });
    }
  }, [editedRecord, stateCityPTData]);
  const handleStateRegion = (type, id) => {
    if (stateCityPTData) {
      if (type === "state") {
        setStateData({
          load: false,
          values: stateCityPTData?.state_result || [],
        });
      } else if (type === "region") {
        if(id){
          setRegionData({
            load: false,
            values:
              stateCityPTData?.result?.filter((el) => el?.state_id === id) || [],
          });
        } else {
           setRegionData({
            load: false,
            values: [],
          });
        }
      }
    }
  };

  const validate = (fieldValues = form) => {
    let temp = { ...errors };

    if ("work_location_name" in fieldValues)
      temp.work_location_name = fieldValues.work_location_name?.trim()
        ? ""
        : "Work location name is required";

    if ("state_id" in fieldValues)
      temp.state_id = fieldValues.state_id ? "" : "State is required";

    if ("professional_tax_region_id" in fieldValues)
      temp.professional_tax_region_id = fieldValues.professional_tax_region_id
        ? ""
        : "Region is required";
    if ("radius" in fieldValues) {
        if (!fieldValues.radius) {
        temp.radius = "Radius is required";
        } else if (Number(fieldValues.radius) < 10) {
        temp.radius = "Radius should be at least 10 meters";
        } else if (Number(fieldValues.radius) > 5000) {
        temp.radius = "Radius should not exceed 5000 meters";
        } else {
        temp.radius = "";
        }
    }
    if ("lat_long" in fieldValues) {
      temp.lat_long = isValidLatLong(fieldValues.lat_long)
        ? ""
        : "Enter valid coordinates (lat,long)";
    }

    setErrors({ ...temp });

    return Object.values(temp).every((x) => x === "");
  };
  const handleInput = (event, value) => {
    if (["state_id"].includes(event)) {
      if (value) {
        setForm({
          ...form,
          [event]: value,
          professional_tax_region_id: "",
          lat_long: ""
        });
        setLatLongInput("");
        setRegionData({
          load: false,
          values:
            stateCityPTData?.result?.filter(
              (el) => el?.state_id === value?.state_id
            ) || [],
        });
        const center = STATE_CAPITAL_CENTERS[value.state_name];
        if (center) {
            setForm((prev) => ({
                ...prev,
                lat_long: center,
            }));
            setLatLongInput(center);
        }
      } else {
        setForm({ ...form, [event]: "" });
      }
      Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
        validate({ ...form, [event]: value ? value : "" });
    } else if (["professional_tax_region_id"].includes(event)) {
      if (value) {
        setForm({ ...form, [event]: value });
      } else {
        setForm({ ...form, [event]: "" });
      }
      Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
        validate({ ...form, [event]: value ? value : "" });
    } else if (["is_default"].includes(event?.target?.name)) {
      setForm({ ...form, [event.target.name]: event.target.checked });
    } else {
      setForm({
        ...form,
        [event.target.name]: event.target.value,
      });
      Object.values?.(errors)?.find((res) => res !== "")?.length > 0 &&
        validate({
          ...form,
          [event.target.name]: event.target.value,
        });
    }
  };

  const handleSubmit = async(e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    let locationName="";
    try {
      if(form?.lat_long){
        const [lat,lng] = form?.lat_long?.split(",")

        const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
              );
        const data = await res.json();
        if (data?.display_name) {
          locationName= data?.display_name
        }
      }
    } catch (error) {
      console.error("Geo error:", error.message);
      toast.error("Geo error while finding location")
    }
    if (editedRecord) {
      dispatch(UpdateWorkLocationAction({...form,
        location_name: locationName
      }));
    } else {
      dispatch(AddWorkLocationAction({...form,
        location_name: locationName
      }));
    }
    setSaving(false);
    setOpenPopup(false);
  };
  return (
    <>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              required
              value={form?.work_location_name}
              onChange={handleInput}
              variant="outlined"
              fullWidth
              id="work_location_name"
              label="Work Location Name"
              name="work_location_name"
              autoComplete="work_location_name"
              {...(errors.work_location_name && {
                error: true,
                helperText: errors.work_location_name,
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.is_default || false}
                  name="is_default"
                  onChange={handleInput}
                  disabled={(workLocation_options?.length || 0) === 0}
                />
              }
              label="Set as Default Location"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <SelectableSearch
                isOptionEqualToValue={(option, value) =>
                  option?.state_id === value?.state_id
                }
                fullWidth
                required
                variant="outlined"
                size="small"
                loading={() => handleStateRegion("state")}
                value={form?.state_id}
                label="State"
                fieldLabel="state_name"
                name="state_id"
                onChangeEvent={handleInput}
                data={stateData}
                {...(errors.state_id && {
                  error: true,
                  helperText: errors.state_id,
                })}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <SelectableSearch
                isOptionEqualToValue={(option, value) =>
                  +option?.professional_tax_region_id ===
                  +value?.professional_tax_region_id
                }
                fullWidth
                required
                variant="outlined"
                size="small"
                loading={() =>
                  handleStateRegion("region", form?.state_id?.state_id)
                }
                value={form.professional_tax_region_id}
                fieldLabel="region_name"
                name="professional_tax_region_id"
                label="Region"
                onChangeEvent={handleInput}
                noOptionText={!form?.state_id ? "Please select state first" : "No Options"}
                data={regionData}
                {...(errors.professional_tax_region_id && {
                  error: true,
                  helperText: errors.professional_tax_region_id,
                })}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              required
              value={form?.radius}
              onChange={handleInput}
              variant="outlined"
              fullWidth
              id="radius"
              label="Radius (Meters)"
              type="number"
              name="radius"
               InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Employees mapped to this work location can mark attendance only within this distance (in meters).">
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ cursor: "pointer", color: "text.secondary" }}
                      />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              {...(errors.radius && {
                error: true,
                helperText: errors.radius,
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              value={latLongInput}
              variant="outlined"
              fullWidth
              label="Location Coordinates (Lat, Long)"
              placeholder="12.9716,77.5946"
              onChange={(e) => {
                const value = e.target.value;
                setLatLongInput(value);

                if (isValidLatLong(value)) {
                  setErrors((prev) => ({ ...prev, lat_long: "" }));

                  // only update map value when valid
                  setForm((prev) => ({
                    ...prev,
                    lat_long: value,
                  }));
                } else {
                  setForm((prev) => ({
                    ...prev,
                    lat_long: "",
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    lat_long: "Enter valid coordinates (lat,long)",
                  }));
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Enter latitude and longitude separated by comma">
                      <InfoOutlinedIcon fontSize="small" sx={{ cursor: "pointer" }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              {...(errors.lat_long && {
                error: true,
                helperText: errors.lat_long,
              })}
            />
          </Grid>
          <Divider sx={{ pt: 2, pl: 2, width: "100%", margin: "auto", borderBottomWidth: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Map Preview
            </Typography>
          </Divider>
          <Grid item xs={12}> 
              <GeoLocationPicker
                  value={form.lat_long}
                  radius={form.radius}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, lat_long: val }));
                    setLatLongInput(val);
                    setErrors((prev) => ({ ...prev, lat_long: "" }));
                  }}
                  expand={expand}
                  setExpand={setExpand}
              />
          </Grid>
        </Grid>
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            size="medium"
            className="mpl-primary-btn"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} disableShrink /> : null}
          >
            {editedRecord ? "Update" : "Save"}
          </Button>
        </Box>
      </Box>
      <Popup
        fullWidth={true}
        maxWidth="md"
        openPopup={expand}
        setOpenPopup={setExpand}
      >
        <GeoLocationPicker
            value={form.lat_long}
            radius={form.radius}
            maxheight={"70vh"}
            onChange={(val) => {
              setForm((prev) => ({ ...prev, lat_long: val }));
              setLatLongInput(val);
              setErrors((prev) => ({ ...prev, lat_long: "" }));
            }}
            expand={expand}
            setExpand={setExpand}
        />
      </Popup>
    </>
  );
};
const STATE_CAPITAL_CENTERS = {
  "Andaman and Nicobar": "11.6234,92.7265",      // Port Blair
  "Andhra Pradesh": "16.5062,80.6480",           // Amaravati
  "Arunachal Pradesh": "27.0844,93.6053",        // Itanagar
  "Assam": "26.1445,91.7362",                    // Dispur
  "Bihar": "25.5941,85.1376",                    // Patna
  "Chandigarh": "30.7333,76.7794",               // Chandigarh
  "Chattisgarh": "21.2514,81.6296",              // Raipur
  "Dadra and Nagar Haveli": "20.2739,73.0083",   // Silvassa
  "Daman and Diu": "20.3974,72.8328",            // Daman
  "Delhi": "28.6139,77.2090",                    // New Delhi
  "Goa": "15.4909,73.8278",                      // Panaji
  "Gujarat": "23.2156,72.6369",                  // Gandhinagar
  "Haryana": "30.7333,76.7794",                  // Chandigarh
  "Himachal Pradesh": "31.1048,77.1734",         // Shimla
  "Jammu and Kashmir": "34.0837,74.7973",        // Srinagar
  "Jharkhand": "23.3441,85.3096",                // Ranchi
  "Karnataka": "12.9716,77.5946",                // Bengaluru
  "Kerala": "8.5241,76.9366",                    // Thiruvananthapuram
  "Lakshadweep": "10.5667,72.6417",              // Kavaratti
  "Madhya Pradesh": "23.2599,77.4126",           // Bhopal
  "Maharashtra": "19.0760,72.8777",              // Mumbai
  "Manipur": "24.8170,93.9368",                  // Imphal
  "Meghalaya": "25.5788,91.8933",                // Shillong
  "Mizoram": "23.7271,92.7176",                  // Aizawl
  "Nagaland": "25.6751,94.1086",                 // Kohima
  "Orissa": "20.2961,85.8245",                   // Bhubaneswar
  "Puducherry": "11.9416,79.8083",               // Puducherry
  "Punjab": "30.7333,76.7794",                   // Chandigarh
  "Rajasthan": "26.9124,75.7873",                // Jaipur
  "Sikkim": "27.3314,88.6138",                   // Gangtok
  "Tamil Nadu": "13.0827,80.2707",               // Chennai
  "Telangana": "17.3850,78.4867",                // Hyderabad
  "Tripura": "23.8315,91.2868",                  // Agartala
  "Uttar Pradesh": "26.8467,80.9462",            // Lucknow
  "Uttarakhand": "30.3165,78.0322",              // Dehradun
  "West Bengal": "22.5726,88.3639",              // Kolkata
};
const isValidLatLong = (value) => {
  if (!value) return false;

  const parts = value?.split(",");
  if (parts.length !== 2) return false;

  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());

  if (isNaN(lat) || isNaN(lng)) return false;
  if (lat < -90 || lat > 90) return false;
  if (lng < -180 || lng > 180) return false;

  return true;
};

export default WorkGeoLocationConfig;
